<template>
  <section id="section2" class="fact-sheets">
    <div class="container">
      <header class="fact-sheets__head">
        <p class="fact-sheets__eyebrow section-label" data-aos="fade-up">
          <span class="section-label__text">{{ $t('ServiceFive.subtitle') }}</span>
        </p>
        <h2 class="fact-sheets__title" data-aos="fade-up" data-aos-delay="50">
          {{ $t('ServiceFive.title') }}
        </h2>
        <p class="fact-sheets__lead" data-aos="fade-up" data-aos-delay="100">
          {{ $t('ServiceFive.description') }}
        </p>
      </header>

      <!-- AOS lives on a wrapper: Vue rewrites the card's class attribute whenever the
           autoplay state changes, which would strip aos-animate and hide the card -->
      <div data-aos="fade-up" data-aos-delay="150">
        <div
          ref="card"
          class="fs-card"
          :class="{ 'is-auto': autoplayAllowed && !stopped, 'is-playing': playing }"
          @mouseenter="hovered = true"
          @mouseleave="hovered = false"
          @focusin="onFocusIn"
          @focusout="focused = false"
        >
          <div
            class="fs-card__list"
            role="tablist"
            aria-orientation="vertical"
            @keydown="onKeydown"
          >
            <div
              v-for="(item, index) in items"
              :key="index"
              class="fs-item"
              :class="{ 'is-active': index === active }"
            >
              <button
                :id="`fs-tab-${index}`"
                ref="tabs"
                type="button"
                role="tab"
                class="fs-item__tab"
                :aria-selected="index === active ? 'true' : 'false'"
                aria-controls="fs-panel"
                :tabindex="index === active ? 0 : -1"
                @click="onTabClick(index, $event)"
              >
                <span
                  v-if="index === active"
                  :key="`bar-${active}-${cycle}`"
                  class="fs-item__bar"
                  @animationend="next"
                />
                <svg
                  class="fs-item__icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  v-html="icons[index]"
                />
                <span class="fs-item__label">{{ item.title }}</span>
                <svg class="fs-item__chevron" viewBox="0 0 24 24" aria-hidden="true">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>

              <!-- Mobile: the panel opens inline under its row -->
              <div v-if="index === active" class="fs-item__inline">
                <div class="fs-media">
                  <img :src="images[index]" :alt="item.title" loading="lazy" />
                </div>
                <p class="fs-panel__desc">{{ item.description }}</p>
              </div>
            </div>
          </div>

          <div
            id="fs-panel"
            class="fs-card__panel"
            role="tabpanel"
            :aria-labelledby="`fs-tab-${active}`"
          >
            <p class="fs-panel__count" aria-hidden="true">
              <span class="fs-panel__count-now">
                <transition name="fs-count">
                  <span :key="active">{{ pad(active + 1) }}</span>
                </transition>
              </span>
              <span class="fs-panel__count-sep">/</span>
              <span>{{ pad(items.length) }}</span>
            </p>

            <!-- Every illustration stays mounted (so it is loaded before its turn); the
               incoming one fades in on top of the outgoing one -->
            <div class="fs-media">
              <img
                v-for="(src, index) in images"
                :key="index"
                :src="src"
                :alt="index === active ? current.title : ''"
                class="fs-media__img"
                :class="{ 'is-active': index === active }"
                :aria-hidden="index === active ? 'false' : 'true'"
                loading="lazy"
                decoding="async"
              />
            </div>

            <!-- All copies share one grid cell so the card keeps the tallest height -->
            <div class="fs-panel__stack">
              <div
                v-for="(item, index) in items"
                :key="index"
                class="fs-panel__copy"
                :class="{ 'is-active': index === active }"
                :aria-hidden="index === active ? 'false' : 'true'"
              >
                <h3 class="fs-panel__title">{{ item.title }}</h3>
                <p class="fs-panel__desc">{{ item.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="fact-sheets__summary" data-aos="fade-up">{{ $t('ServiceFive.explain') }}</p>
    </div>
  </section>
</template>

<script>
const AUTOPLAY_MS = 6000

// Feather-style outline paths (lightbulb and landmark are not in feather-icons)
const ICONS = [
  '<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>',
  '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>',
  '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/>',
  '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>',
  '<path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/>',
  '<polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>',
  '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>',
  '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  '<line x1="3" y1="22" x2="21" y2="22"/><line x1="6" y1="18" x2="6" y2="11"/><line x1="10" y1="18" x2="10" y2="11"/><line x1="14" y1="18" x2="14" y2="11"/><line x1="18" y1="18" x2="18" y2="11"/><polygon points="12 2 20 7 4 7"/>'
]

const IMAGES = [
  require('@/assets/images/Newlandindpage/fact-sheets/01-accessibility-convenience.jpg'),
  require('@/assets/images/Newlandindpage/fact-sheets/02-expanded-coverage.jpg'),
  require('@/assets/images/Newlandindpage/fact-sheets/03-reduced-range-anxiety.jpg'),
  require('@/assets/images/Newlandindpage/fact-sheets/04-competition-pricing.jpg'),
  require('@/assets/images/Newlandindpage/fact-sheets/05-innovation-technology.jpg'),
  require('@/assets/images/Newlandindpage/fact-sheets/06-resilience-redundancy.jpg'),
  require('@/assets/images/Newlandindpage/fact-sheets/07-interoperability-standards.jpg'),
  require('@/assets/images/Newlandindpage/fact-sheets/08-global-travel.jpg'),
  require('@/assets/images/Newlandindpage/fact-sheets/09-government-support.jpg')
]

export default {
  name: 'FactSheets',
  data() {
    return {
      active: 0,
      cycle: 0,
      icons: ICONS,
      images: IMAGES,
      hovered: false,
      focused: false,
      inView: false,
      pageVisible: true,
      stopped: false,
      autoplayAllowed: false
    }
  },
  computed: {
    items() {
      return this.$t('ServiceFive.items')
    },
    current() {
      return this.items[this.active] || {}
    },
    // The progress bar runs (and advances on animationend) only while this is true
    playing() {
      return (
        this.autoplayAllowed &&
        !this.stopped &&
        this.inView &&
        this.pageVisible &&
        !this.hovered &&
        !this.focused
      )
    }
  },
  mounted() {
    this.$el.style.setProperty('--fs-autoplay', `${AUTOPLAY_MS}ms`)

    // Autoplay only on desktop layouts and when the visitor accepts motion
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
    const desktop = window.matchMedia('(min-width: 992px)')
    this.updateAutoplay = () => {
      this.autoplayAllowed = desktop.matches && !reduce.matches
    }
    this.updateAutoplay()
    this.mediaQueries = [reduce, desktop]
    this.mediaQueries.forEach((mq) =>
      mq.addEventListener
        ? mq.addEventListener('change', this.updateAutoplay)
        : mq.addListener(this.updateAutoplay)
    )

    this.onVisibility = () => {
      this.pageVisible = !document.hidden
    }
    document.addEventListener('visibilitychange', this.onVisibility)

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.inView = entry.isIntersecting
        },
        { threshold: 0.35 }
      )
      this.observer.observe(this.$refs.card)
    } else {
      this.inView = true
    }
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
    document.removeEventListener('visibilitychange', this.onVisibility)
    this.mediaQueries.forEach((mq) =>
      mq.removeEventListener
        ? mq.removeEventListener('change', this.updateAutoplay)
        : mq.removeListener(this.updateAutoplay)
    )
  },
  methods: {
    pad(n) {
      return String(n).padStart(2, '0')
    },
    // Keyboard navigation ends the tour, so it never moves away from what is being read
    select(index) {
      this.stopped = true
      this.go(index)
    },
    // A mouse click only jumps there and restarts the timer; it resumes once the pointer leaves.
    // Enter/Space also fire click (with detail 0) and count as keyboard use.
    onTabClick(index, event) {
      if (event.detail === 0) this.select(index)
      else this.go(index)
    },
    // Clicking a tab focuses it in some browsers; only keyboard focus should hold the tour,
    // otherwise it stays paused until the visitor clicks somewhere else on the page
    onFocusIn(event) {
      try {
        this.focused = event.target.matches(':focus-visible')
      } catch (e) {
        this.focused = true
      }
    },
    go(index) {
      const count = this.items.length
      this.active = (index + count) % count
      this.cycle += 1
    },
    next() {
      if (this.playing) this.go(this.active + 1)
    },
    onKeydown(event) {
      const moves = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }
      let target
      if (event.key in moves) target = this.active + moves[event.key]
      else if (event.key === 'Home') target = 0
      else if (event.key === 'End') target = this.items.length - 1
      else return

      event.preventDefault()
      this.select(target)
      this.$nextTick(() => this.$refs.tabs[this.active].focus())
    }
  }
}
</script>
